import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Phone as PhoneIcon, ShieldCheck } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { usePageCode } from "../../config/PageContext";
import { trackFormStart, trackGenerateLead } from "../../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CallbackForm({
  source = "Hero",
  ctaLabel = "Chcę bezpłatną wycenę",
  helperText = "Jeden doradca · Bez spamu · Bez zobowiązań",
  variant = "light", // light | onDark
  inputId,
  testIdPrefix = "callback",
}) {
  const pageCode = usePageCode();
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const cleaned = phone.replace(/[\s\-\(\)]/g, "");
    if (!/^(\+48)?\d{9}$/.test(cleaned)) {
      setError("Wpisz poprawny numer (9 cyfr)");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/callback`, {
        phone: cleaned,
        page: pageCode,
        source,
        url: typeof window !== "undefined" ? window.location.href : null,
      });
      trackGenerateLead(source.toLowerCase());
      setSuccess(true);
      setPhone("");
      toast.success("Dziękujemy! Skontaktujemy się wkrótce.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || "Coś poszło nie tak. Spróbuj ponownie.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className={`rounded-2xl p-5 sm:p-6 border ${
          variant === "onDark"
            ? "bg-white/10 border-white/20 text-white"
            : "bg-emerald-50 border-emerald-200 text-emerald-900"
        }`}
        data-testid={`${testIdPrefix}-success`}
      >
        <div className="flex items-center gap-3">
          <div className={`grid place-items-center h-10 w-10 rounded-full ${
            variant === "onDark" ? "bg-brand-yellow text-brand-yellowFg" : "bg-emerald-600 text-white"
          }`}>
            <PhoneIcon className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading font-bold text-lg leading-tight">
              Dziękujemy! Oddzwonimy w 5–10 minut.
            </p>
            <p className={`text-sm mt-0.5 ${variant === "onDark" ? "text-white/80" : "text-emerald-800/80"}`}>
              Bez spamu, bez zobowiązań.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const isDark = variant === "onDark";

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className={`w-full ${isDark ? "" : ""}`}
      data-testid={`${testIdPrefix}-form`}
    >
      <div className={`flex flex-col sm:flex-row gap-2 sm:gap-2 p-2 rounded-2xl ${
        isDark ? "bg-white/10 border border-white/15" : "bg-white border border-brand-border shadow-md"
      }`}>
        <div className="flex-1 flex items-center gap-2 px-3">
          <PhoneIcon className={`h-5 w-5 flex-shrink-0 ${isDark ? "text-brand-yellow" : "text-brand-blue"}`} />
          <Input
            id={inputId}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="np. 600 000 000"
            value={phone}
            onFocus={() => trackFormStart(source.toLowerCase())}
            onChange={(e) => {
              trackFormStart(source.toLowerCase());
              setPhone(e.target.value);
              if (error) setError("");
            }}
            data-testid={`${testIdPrefix}-phone-input`}
            aria-label="Numer telefonu"
            className={`h-12 sm:h-14 border-0 shadow-none px-0 text-base sm:text-lg font-medium focus-visible:ring-0 focus-visible:ring-offset-0 ${
              isDark ? "bg-transparent text-white placeholder:text-white/50" : "bg-transparent text-brand-ink"
            }`}
          />
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid={`${testIdPrefix}-submit-btn`}
          className="h-12 sm:h-14 rounded-xl bg-brand-yellow hover:bg-brand-yellowHover text-brand-yellowFg font-bold px-5 sm:px-6 text-base shadow-md hover:shadow-lg active:scale-95 transition-all disabled:opacity-70"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wysyłanie…
            </>
          ) : (
            <>
              {ctaLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
      {error && (
        <p className={`mt-2 text-sm ${isDark ? "text-amber-200" : "text-red-600"}`} data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}
      {helperText && (
        <div className={`mt-3 flex items-center gap-2 text-xs sm:text-sm ${
          isDark ? "text-white/70" : "text-brand-muted"
        }`}>
          <ShieldCheck className={`h-4 w-4 ${isDark ? "text-brand-yellow" : "text-emerald-600"}`} />
          <span>{helperText}</span>
        </div>
      )}
    </form>
  );
}
